import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const AlertCorrelationAnalysis = ({ events, categories }) => {
  const correlations = useMemo(() => {
    // Analyze event patterns
    const patterns = [];

    // Payment failures correlation with signups
    if (categories?.paymentFailures?.count > 5 && categories?.newSignups?.count > 50) {
      patterns?.push({
        id: 1,
        title: 'Payment Failures Spike During High Signup Volume',
        description: 'Increased payment failures detected during peak signup periods. May indicate payment gateway capacity issues.',
        severity: 'High',
        recommendation: 'Review payment gateway configuration and consider load balancing.',
        confidence: 87
      });
    }

    // Premium conversion correlation
    if (categories?.premiumConversions?.count > 0 && categories?.featureSpikes?.count > categories?.featureSpikes?.threshold) {
      patterns?.push({
        id: 2,
        title: 'Premium Feature Usage Correlates with Conversions',
        description: 'High Premium feature usage observed alongside conversion events. Positive engagement indicator.',
        severity: 'Low',
        recommendation: 'Continue monitoring feature adoption patterns to optimize conversion funnel.',
        confidence: 92
      });
    }

    // System anomalies pattern
    if (categories?.systemAnomalies?.count > 3) {
      patterns?.push({
        id: 3,
        title: 'Multiple System Anomalies Detected',
        description: 'Recurring system anomalies may indicate underlying infrastructure issues or security concerns.',
        severity: 'Critical',
        recommendation: 'Immediate investigation required. Check server logs and security audit trail.',
        confidence: 78
      });
    }

    // Predictive indicator: Low conversion with high signups
    if (categories?.newSignups?.count > 30 && categories?.premiumConversions?.count < 3) {
      patterns?.push({
        id: 4,
        title: 'Low Conversion Rate Despite High Signup Volume',
        description: 'Signup velocity is high but conversion rate is below expected threshold. Potential onboarding or pricing issue.',
        severity: 'Medium',
        recommendation: 'Review onboarding flow and Premium feature visibility. Consider A/B testing pricing presentation.',
        confidence: 85
      });
    }

    return patterns;
  }, [events, categories]);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'text-destructive';
      case 'High': return 'text-warning';
      case 'Medium': return 'text-primary';
      case 'Low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'Critical': return 'bg-destructive/10 border-destructive/20';
      case 'High': return 'bg-warning/10 border-warning/20';
      case 'Medium': return 'bg-primary/10 border-primary/20';
      case 'Low': return 'bg-success/10 border-success/20';
      default: return 'bg-muted/10 border-border';
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="bg-muted/50 px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="GitBranch" size={20} className="text-primary" strokeWidth={2} />
            <h2 className="text-lg font-semibold text-foreground">Alert Correlation Analysis</h2>
          </div>
          <span className="text-sm text-muted-foreground">{correlations?.length} patterns detected</span>
        </div>
      </div>

      <div className="p-6">
        {correlations?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Icon name="TrendingUp" size={48} className="text-muted-foreground mb-3" strokeWidth={1.5} />
            <p className="text-muted-foreground">No significant patterns detected</p>
            <p className="text-xs text-muted-foreground mt-1">Continue monitoring for correlations</p>
          </div>
        ) : (
          <div className="space-y-4">
            {correlations?.map((pattern) => (
              <div
                key={pattern?.id}
                className={`border rounded-lg p-4 ${getSeverityBg(pattern?.severity)}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-foreground">{pattern?.title}</h3>
                      <span className={`text-xs font-semibold ${getSeverityColor(pattern?.severity)}`}>
                        {pattern?.severity}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{pattern?.description}</p>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 bg-background rounded-full">
                    <Icon name="Zap" size={12} className="text-primary" strokeWidth={2} />
                    <span className="text-xs font-semibold text-foreground">{pattern?.confidence}%</span>
                  </div>
                </div>

                <div className="bg-background/50 rounded-lg p-3 flex items-start gap-2">
                  <Icon name="Lightbulb" size={16} className="text-primary flex-shrink-0 mt-0.5" strokeWidth={2} />
                  <div>
                    <h4 className="text-xs font-semibold text-foreground mb-1">Recommendation</h4>
                    <p className="text-xs text-muted-foreground">{pattern?.recommendation}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-1">About Correlation Analysis</h4>
            <p className="text-xs text-muted-foreground">
              This analysis identifies patterns between different alert types to help predict potential issues before they become critical. 
              Confidence scores indicate the statistical reliability of each pattern.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertCorrelationAnalysis;